import { useEffect, useMemo, useState } from "react";
import { CalendarOff, Plus, Trash2, RefreshCw, CalendarClock } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "@/components/ui/dialog";
import {
  Table, TableHeader, TableRow, TableHead, TableBody, TableCell,
} from "@/components/ui/table";
import { api, apiErrorMessage, formatTanggalID } from "@/lib/api";

const NAMA_HARI = ["Senin", "Selasa", "Rabu", "Kamis", "Jumat", "Sabtu", "Minggu"];

function hariFromISO(iso) {
  try {
    const d = new Date(iso + "T00:00:00");
    const w = (d.getDay() + 6) % 7; // 0=Sen ... 6=Min
    return NAMA_HARI[w];
  } catch {
    return "";
  }
}

export default function AdminHolidays() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [tanggal, setTanggal] = useState("");
  const [nama, setNama] = useState("");
  const [saving, setSaving] = useState(false);
  const [year, setYear] = useState(new Date().getFullYear());

  async function load() {
    setLoading(true);
    try {
      const start = `${year}-01-01`;
      const end = `${year}-12-31`;
      const { data } = await api.get("/master/holidays", { params: { start_date: start, end_date: end } });
      setRows(data);
    } catch (e) {
      toast.error(apiErrorMessage(e));
    } finally {
      setLoading(false);
    }
  }
  useEffect(() => { load(); }, [year]);

  const stats = useMemo(() => {
    const total = rows.length;
    const weekend = rows.filter((r) => {
      const w = hariFromISO(r.tanggal);
      return w === "Sabtu" || w === "Minggu";
    }).length;
    return { total, weekend, weekday: total - weekend };
  }, [rows]);

  async function submit() {
    if (!tanggal) {
      toast.error("Tanggal wajib diisi");
      return;
    }
    if (nama.trim().length < 2) {
      toast.error("Nama hari libur minimal 2 karakter");
      return;
    }
    setSaving(true);
    try {
      await api.post("/master/holidays", { tanggal, nama: nama.trim() });
      toast.success("Hari libur ditambahkan");
      setOpen(false);
      setTanggal("");
      setNama("");
      await load();
    } catch (e) {
      toast.error(apiErrorMessage(e));
    } finally {
      setSaving(false);
    }
  }

  async function del(row) {
    if (!window.confirm(`Hapus hari libur "${row.nama}" (${formatTanggalID(row.tanggal)})?`)) return;
    try {
      await api.delete(`/master/holidays/${row.id}`);
      toast.success("Dihapus");
      await load();
    } catch (e) {
      toast.error(apiErrorMessage(e));
    }
  }

  const years = [];
  const nowY = new Date().getFullYear();
  for (let y = nowY - 1; y <= nowY + 2; y++) years.push(y);

  return (
    <div className="p-4 sm:p-8 max-w-5xl mx-auto" data-testid="page-holidays">
      <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-4">
        <div>
          <div className="text-[11px] font-bold uppercase tracking-[0.24em] text-teal-700">
            Administrasi
          </div>
          <h1 className="font-display text-3xl sm:text-4xl font-extrabold text-slate-900 mt-1 leading-tight">
            Hari Libur Nasional
          </h1>
          <p className="text-slate-500 text-sm mt-1">
            Daftar hari libur ini akan dilewati saat menghitung hari kerja efektif untuk kuota Cuti dan status Alpha.
          </p>
        </div>
        <div className="flex gap-2">
          <select
            value={year}
            onChange={(e) => setYear(parseInt(e.target.value, 10))}
            className="rounded-md border border-slate-200 px-3 py-2 text-sm font-semibold text-slate-800 bg-white"
            data-testid="holiday-year-select"
          >
            {years.map((y) => (<option key={y} value={y}>{y}</option>))}
          </select>
          <Button variant="outline" onClick={load} disabled={loading} className="gap-2" data-testid="holiday-refresh-btn">
            <RefreshCw size={14} /> Muat Ulang
          </Button>
          <Button
            onClick={() => { setTanggal(""); setNama(""); setOpen(true); }}
            className="bg-teal-700 hover:bg-teal-800 gap-2"
            data-testid="holiday-add-btn"
          >
            <Plus size={14} /> Tambah Hari Libur
          </Button>
        </div>
      </div>

      <div className="mt-6 grid grid-cols-1 sm:grid-cols-3 gap-4">
        <StatCard icon={CalendarOff} label="Total Hari Libur" value={stats.total} tone="teal" />
        <StatCard icon={CalendarClock} label="Hari Kerja (Sen–Jum)" value={stats.weekday} tone="emerald" />
        <StatCard icon={CalendarOff} label="Akhir Pekan" value={stats.weekend} tone="amber" />
      </div>

      <div className="mt-6 rounded-xl border border-slate-200 bg-white overflow-hidden">
        <div className="overflow-x-auto">
          <Table>
            <TableHeader>
              <TableRow className="bg-slate-50 hover:bg-slate-50">
                <TableHead className="uppercase text-[10px] tracking-widest font-bold text-slate-500">Tanggal</TableHead>
                <TableHead className="uppercase text-[10px] tracking-widest font-bold text-slate-500">Hari</TableHead>
                <TableHead className="uppercase text-[10px] tracking-widest font-bold text-slate-500">Nama Hari Libur</TableHead>
                <TableHead className="uppercase text-[10px] tracking-widest font-bold text-slate-500 text-right">Aksi</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {loading ? (
                <TableRow><TableCell colSpan={4} className="text-center py-10 text-slate-500">Memuat…</TableCell></TableRow>
              ) : rows.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={4} className="text-center py-14">
                    <div className="mx-auto w-14 h-14 rounded-full bg-slate-100 flex items-center justify-center text-slate-400">
                      <CalendarOff size={22} />
                    </div>
                    <div className="mt-3 font-semibold text-slate-700">Belum ada hari libur untuk {year}</div>
                    <div className="text-xs text-slate-500 mt-1">Klik "Tambah Hari Libur" untuk memulai.</div>
                  </TableCell>
                </TableRow>
              ) : (
                rows.map((r) => (
                  <TableRow key={r.id} data-testid={`holiday-row-${r.id}`} className="border-b border-slate-100 hover:bg-slate-50/60">
                    <TableCell className="font-mono-clock font-semibold text-slate-900 whitespace-nowrap">{r.tanggal}</TableCell>
                    <TableCell className="text-sm text-slate-700">{hariFromISO(r.tanggal)}</TableCell>
                    <TableCell className="font-semibold text-slate-900">{r.nama}</TableCell>
                    <TableCell className="text-right">
                      {/* Tombol Hapus dihilangkan (Keamanan Data — Feb 2026). */}
                      <span className="text-xs text-slate-400 italic">—</span>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </div>
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent data-testid="holiday-dialog">
          <DialogHeader>
            <DialogTitle>Tambah Hari Libur Nasional</DialogTitle>
            <DialogDescription>
              Tanggal ini akan dilewati saat menghitung hari kerja efektif.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label className="text-xs font-bold uppercase tracking-wider text-slate-600">Tanggal</Label>
              <Input
                type="date"
                className="mt-1.5"
                value={tanggal}
                onChange={(e) => setTanggal(e.target.value)}
                data-testid="holiday-input-tanggal"
              />
              {tanggal ? (
                <div className="text-[11px] text-slate-500 mt-1">Hari: <b>{hariFromISO(tanggal)}</b></div>
              ) : null}
            </div>
            <div>
              <Label className="text-xs font-bold uppercase tracking-wider text-slate-600">Nama Hari Libur</Label>
              <Input
                className="mt-1.5"
                placeholder="Contoh: HUT Kemerdekaan RI"
                value={nama}
                onChange={(e) => setNama(e.target.value)}
                data-testid="holiday-input-nama"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)}>Batal</Button>
            <Button onClick={submit} disabled={saving} className="bg-teal-700 hover:bg-teal-800" data-testid="holiday-save-btn">
              {saving ? "Menyimpan…" : "Simpan"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function StatCard({ icon: Icon, label, value, tone = "teal" }) {
  const tones = {
    teal: "bg-teal-50 border-teal-200 text-teal-700",
    emerald: "bg-emerald-50 border-emerald-200 text-emerald-700",
    amber: "bg-amber-50 border-amber-200 text-amber-700",
  };
  return (
    <div className={`rounded-xl border p-4 flex items-start gap-3 ${tones[tone]}`}>
      <div className="w-10 h-10 rounded-lg bg-white/70 border border-current flex items-center justify-center shrink-0">
        <Icon size={18} />
      </div>
      <div>
        <div className="text-[10px] uppercase tracking-[0.22em] font-bold">{label}</div>
        <div className="font-mono-clock text-2xl font-bold mt-0.5 text-slate-900">{value}</div>
      </div>
    </div>
  );
}
